/*
  AllergiesPanel — spec §12. Severity: mild / moderate / severe /
  anaphylaxis. Severe + anaphylaxis auto-surface in the critical-alerts
  header banner (see CriticalAlertsStrip).
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkle, Plus, Pencil, ArchiveRestore, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const SEVERITY_TONE = {
  anaphylaxis: "border-red-500 bg-red-50 text-red-950",
  severe:      "border-red-400 bg-red-50 text-red-900",
  moderate:    "border-amber-300 bg-amber-50 text-amber-900",
  mild:        "border-sky-200 bg-sky-50 text-sky-900",
};
const SEVERITY_LABEL = {
  anaphylaxis: "Anaphylaxis",
  severe: "Severe",
  moderate: "Moderate",
  mild: "Mild",
};
const SOURCES = [
  { value: "client_reported", label: "Client-reported" },
  { value: "clinician_confirmed", label: "Clinician-confirmed" },
  { value: "imported", label: "Imported" },
  { value: "migrated", label: "Migrated" },
];

export default function AllergiesPanel({ email }) {
  const { can } = useAuth();
  const canArchive = can("owner", "manager");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const encoded = encodeURIComponent(email || "");

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const r = await api.get(`/clients/${encoded}/allergies`);
      setRows(r.data?.allergies || []);
    } catch (e) {
      toast.error("Couldn't load allergies", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [email, encoded]);

  useEffect(() => { load(); }, [load]);

  async function archive(row) {
    if (!confirm(`Archive allergy to "${row.substance}"? Owner/manager can restore later.`)) return;
    try {
      await api.delete(`/allergies/${row.id}`);
      toast.success("Archived");
      await load();
    } catch (e) {
      toast.error("Archive failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="space-y-3" data-testid="allergies-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
          <Sparkle className="h-3.5 w-3.5" /> Allergies &amp; sensitivities
          {rows.length > 0 && (
            <span className="text-zinc-400 normal-case font-normal">· {rows.length}</span>
          )}
        </div>
        <Button
          size="sm" variant="outline"
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          data-testid="allergy-add-btn"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add allergy
        </Button>
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500 py-3 flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-3">
          No allergies recorded. Add substances, reactions and severity — severe / anaphylaxis
          entries auto-surface in the red header banner.
        </div>
      ) : (
        <ul className="flex flex-wrap gap-2" data-testid="allergy-list">
          {rows.map((r) => (
            <li
              key={r.id}
              className={cn(
                "rounded-md border px-3 py-2 max-w-md",
                SEVERITY_TONE[r.severity] || SEVERITY_TONE.moderate,
                !r.active && "opacity-60",
              )}
              data-testid={`allergy-row-${r.id}`}
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <Sparkle className="h-3.5 w-3.5" />
                {r.substance}
                <span className="text-2xs uppercase tracking-wider font-normal opacity-80">
                  · {SEVERITY_LABEL[r.severity] || r.severity}
                </span>
              </div>
              {r.reaction && (
                <div className="text-[0.75rem] mt-0.5 opacity-90">Reaction: {r.reaction}</div>
              )}
              {r.management_instructions && (
                <div className="text-[0.75rem] mt-0.5 opacity-90">{r.management_instructions}</div>
              )}
              <div className="text-2xs mt-1 opacity-70 flex items-center gap-2">
                <span className="capitalize">{r.source?.replace("_", "-")}</span>
                <button
                  type="button"
                  onClick={() => { setEditing(r); setDialogOpen(true); }}
                  className="ml-auto hover:underline"
                  data-testid={`allergy-edit-${r.id}`}
                >
                  <Pencil className="h-3 w-3 inline-block mr-0.5" /> Edit
                </button>
                {canArchive && (
                  <button
                    type="button"
                    onClick={() => archive(r)}
                    className="hover:underline"
                    data-testid={`allergy-archive-${r.id}`}
                  >
                    <ArchiveRestore className="h-3 w-3 inline-block mr-0.5" /> Archive
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {dialogOpen && (
        <AllergyDialog
          email={email}
          existing={editing}
          onClose={() => { setDialogOpen(false); setEditing(null); }}
          onSaved={load}
        />
      )}
    </div>
  );
}

function AllergyDialog({ email, existing, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    substance: existing?.substance || "",
    reaction: existing?.reaction || "",
    severity: existing?.severity || "moderate",
    source: existing?.source || "client_reported",
    date_recorded: existing?.date_recorded || "",
    management_instructions: existing?.management_instructions || "",
  }));
  const [saving, setSaving] = useState(false);
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save() {
    if (!form.substance.trim()) { toast.error("Substance required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, date_recorded: form.date_recorded || null };
      if (existing?.id) {
        await api.patch(`/allergies/${existing.id}`, payload);
      } else {
        await api.post(`/clients/${encodeURIComponent(email)}/allergies`, payload);
      }
      toast.success(existing ? "Updated" : "Added");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="allergy-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit allergy" : "Add allergy"}</DialogTitle>
          <DialogDescription>
            Severe or anaphylaxis-level allergies show in the red header banner across every
            client-context screen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Substance</Label>
            <Input
              value={form.substance}
              onChange={change("substance")}
              placeholder="e.g. Peanuts, Penicillin, Latex, Aspirin"
              className="mt-1"
              data-testid="allergy-field-substance"
            />
          </div>
          <div>
            <Label className="text-xs">Reaction</Label>
            <Input
              value={form.reaction}
              onChange={change("reaction")}
              placeholder="e.g. Anaphylactic shock, Hives, Angioedema"
              className="mt-1"
              data-testid="allergy-field-reaction"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Severity</Label>
              <select
                value={form.severity}
                onChange={change("severity")}
                className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="allergy-field-severity"
              >
                <option value="anaphylaxis">Anaphylaxis (auto-surfaces in header)</option>
                <option value="severe">Severe (auto-surfaces in header)</option>
                <option value="moderate">Moderate</option>
                <option value="mild">Mild</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <select
                value={form.source}
                onChange={change("source")}
                className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="allergy-field-source"
              >
                {SOURCES.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Date recorded</Label>
              <Input type="date" value={form.date_recorded || ""} onChange={change("date_recorded")} className="mt-1" data-testid="allergy-field-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Management instructions</Label>
            <Textarea
              rows={2}
              value={form.management_instructions}
              onChange={change("management_instructions")}
              placeholder="e.g. Carries EpiPen; contact next of kin immediately"
              className="mt-1"
              data-testid="allergy-field-mgmt"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="allergy-save-btn">
            {saving ? "Saving…" : (existing ? "Save changes" : "Add allergy")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
